import React, { useState, useContext } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import moment from "moment";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import { makeStyles, useTheme } from "@mui/styles";
import { useConfirm } from 'material-ui-confirm';
import { NavLink } from "react-router-dom";
import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Table,
  Grid,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Chip,
  Box,
} from "@mui/material";


import { updateSelectedUserIds } from "store/actions/account";
import { useDispatch, useSelector } from "react-redux";
import { ListCard, ListCardField, ListCards } from "components";
import useCompactLayout from "hooks/useCompactLayout";
import ListContext from '../../ListContext';
import SurchargeWeekday from "./SurchargeWeekday";

const useStyles = makeStyles((theme) => ({
  root: {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  content: {
    padding: 0,
  },
  inner: {
    // minWidth: 1050
  },
  nameContainer: {
    display: "flex",
    alignItems: "center",
  },
  avatar: {
    marginRight: theme.spacing(2),
  },
  actions: {
    justifyContent: "flex-end",
    padding: theme.spacing(2),
  },
  textLink: {
    textDecoration: "none",
    color: theme.palette.primary.main,
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  tableHead: {
    backgroundColor: theme.palette.grey[50],
  },
  headerCell: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
}));

const SurchargeTable = (props) => {
  const { className, surcharges, assignTerminal, ...rest } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const isCompact = useCompactLayout();
  const confirm = useConfirm();
  const [selectedItems, setSelectedItems] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [page, setPage] = useState(0);
  const updateUserIds = (ids) => dispatch(updateSelectedUserIds(ids));
  const { setSelectedItemId } = useContext(ListContext);
  const theme = useTheme();

  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const user = useSelector((state) => state.account.user);

  const handleClickTerminal = (terminal) => {
    // setSelectedTerminal(terminal);
    // setOpenAssignDialog(true);
    //show confirm dialog
    //if yes, call assign API

    confirm({ description: `You are about to assign this terminal (${terminal}) to the merchant (${user.restaurantId})` })
    .then(() => {
      assignTerminal(terminal);
    })
    .catch(() => { /* ... */ });

  };

  const handleCloseAssignDialog = () => {
    setOpenAssignDialog(false);
  };

  const handleAssign = async () => {
    // Call your assign API here
    // For example: await assignTerminalToStore(selectedTerminal, selectedStore);
    setOpenAssignDialog(false);
  };

  

  const handleSelectAll = (event) => {
    

    let selectedUsers;

    if (event.target.checked) {
      selectedUsers = surcharges.map((user) => user.id);
    } else {
      selectedUsers = [];
    }

    setSelectedItems(selectedUsers);
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedItems.indexOf(id);
    let newSelectedUsers = [];

    if (selectedIndex === -1) {
      newSelectedUsers = newSelectedUsers.concat(selectedItems, id);
    } else if (selectedIndex === 0) {
      newSelectedUsers = newSelectedUsers.concat(selectedItems.slice(1));
    } else if (selectedIndex === selectedItems.length - 1) {
      newSelectedUsers = newSelectedUsers.concat(selectedItems.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedUsers = newSelectedUsers.concat(
        selectedItems.slice(0, selectedIndex),
        selectedItems.slice(selectedIndex + 1)
      );
    }

    setSelectedItems(newSelectedUsers);
    if(newSelectedUsers.length === 1) {
      setSelectedItemId(newSelectedUsers[0]);
    } else {
      setSelectedItemId(null);
    }
    // setSelectedItemId(id);
    // updateUserIds(newSelectedUsers);
  };

  const handlePageChange = (event, page) => {
    setPage(page);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
  };

  // const filterStores = () => {
  //   if (!searchText || searchText === null || searchText.trim() === "") {
  //     return surcharges;
  //   }
  //   const searchTextLower = searchText.toLowerCase();

  //   return surcharges.filter(
  //     (item) =>
  //       (item.store && item.store.toLowerCase().includes(searchTextLower)) ||
  //       (item.inStoreTerminals && item.inStoreTerminals.map(terminal => terminal.toLowerCase).includes(searchTextLower)) 
  //   );
  // };

  const getTimeString = (date) => {
    if (typeof date === "object" && '_seconds' in date) {
      return moment(new Date(date._seconds * 1000)).format('DD/MM/YYYY')
    }
    return moment(date * 1000).format('DD/MM/YYYY HH:mm')
  };


  // One slice, shared by both layouts, so page state survives a resize.
  const pagedSurcharges = surcharges.slice(page * rowsPerPage, rowsPerPage * page + rowsPerPage);

  const orderTypeChips = (surcharge) =>
    surcharge.orderTypes && surcharge.orderTypes.length > 0 ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {surcharge.orderTypes.map((type) => (
          <Chip
            key={type}
            label={type.charAt(0).toUpperCase() + type.slice(1)}
            size="small"
            color="primary"
            variant="outlined"
          />
        ))}
      </Box>
    ) : (
      <Typography variant="body2" color="textSecondary" fontStyle="italic">
        All types
      </Typography>
    );

  const surchargeDays = (surcharge) =>
    surcharge.weekdays && surcharge.weekdays.length > 0
      ? <SurchargeWeekday weekdays={surcharge.weekdays} weekdayHours={surcharge.weekdayHours} />
      : null;

  const renderCards = () => (
    <ListCards count={surcharges.length} noun="surcharge" empty="No surcharges to show.">
      {pagedSurcharges.map((surcharge, index) => (
        <ListCard
          key={surcharge.id || index}
          data-testid="surcharge-card"
          header={
            <>
              <Checkbox
                checked={selectedItems.indexOf(surcharge.id) !== -1}
                color="primary"
                onChange={(event) => handleSelectOne(event, surcharge.id)}
                size="small"
                inputProps={{ "aria-label": `select ${surcharge.name}` }}
              />
              <NavLink to={`/surcharge/${surcharge.id}`} style={{ flex: 1, minWidth: 0 }}>
                <Typography className={classes.textLink} variant="body1">
                  {surcharge.name}
                </Typography>
              </NavLink>
              <Chip
                label={surcharge.isEnabled ? "Enabled" : "Disabled"}
                color={surcharge.isEnabled ? "success" : "default"}
                size="small"
                variant={surcharge.isEnabled ? "filled" : "outlined"}
              />
            </>
          }
        >
          <ListCardField
            label="Value"
            value={surcharge.isPercentage ? `${surcharge.amount}%` : `$${Number(surcharge.amount).toFixed(2)}`}
          />
          <ListCardField label="Order types" value={orderTypeChips(surcharge)} />
          <ListCardField
            label="Days"
            value={surchargeDays(surcharge)}
          />
        </ListCard>
      ))}
    </ListCards>
  );

  return (
    <Grid item lg={12} md={12} xl={12} xs={12}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <CardContent className={classes.content}>
          {isCompact ? renderCards() : (
          <div className={classes.inner}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.length === surcharges.length}
                        color="primary"
                        indeterminate={
                          selectedItems.length > 0 &&
                          selectedItems.length < surcharges.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell className={classes.headerCell}>Name</TableCell>
                    <TableCell className={classes.headerCell}>Status</TableCell>
                    <TableCell className={classes.headerCell}>Value</TableCell>
                    <TableCell className={classes.headerCell}>Order Types</TableCell>
                    <TableCell className={classes.headerCell}>Applicable Days</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedSurcharges
                    .map((surcharge, index) => (
                      <TableRow
                        className={classes.tableRow}
                        hover
                        key={index}
                      
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              selectedItems.indexOf(surcharge.id) !== -1
                            }
                            color="primary"
                            onChange={(event) =>
                              handleSelectOne(event, surcharge.id)
                            }
                            value="true"
                          />
                        </TableCell>
                        <TableCell>

                        <NavLink to={`/surcharge/${surcharge.id}`} >
                          <Typography className={classes.textLink} variant="body1">
                          {surcharge.name}
                          </Typography>
                          </NavLink>
                        </TableCell>

                        <TableCell>
                          <Chip 
                            label={surcharge.isEnabled ? 'Enabled' : 'Disabled'}
                            color={surcharge.isEnabled ? 'success' : 'default'}
                            size="small"
                            variant={surcharge.isEnabled ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">
                            {surcharge.isPercentage ? `${surcharge.amount}%` : `$${surcharge.amount.toFixed(2)}`}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          {surcharge.orderTypes && surcharge.orderTypes.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {surcharge.orderTypes.map((type) => {
                                // Capitalize first letter for display
                                const displayType = type.charAt(0).toUpperCase() + type.slice(1);
                                return (
                                  <Chip 
                                    key={type} 
                                    label={displayType} 
                                    size="small" 
                                    color="primary"
                                    variant="outlined"
                                  />
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary" fontStyle="italic">
                              All types
                            </Typography>
                          )}
                        </TableCell>
                          
                        <TableCell>
                          {surchargeDays(surcharge)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
            
              </Table>
            </TableContainer>
          </div>
          )}
        </CardContent>
        <CardActions className={classes.actions}>
          <TablePagination
            component="div"
            count={surcharges.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
          />
        </CardActions>
      </Card>
    </Grid>
  );
};

SurchargeTable.propTypes = {
  className: PropTypes.string,
  surcharges: PropTypes.array.isRequired,
};

export default SurchargeTable;
